import { Link } from "react-router-dom";
import DoospLayout from "@/components/doosp/DoospLayout";

// Reusable section wrapper for consistent typography
function Section({ id, title, children }) {
  return (
    <section id={id} className="scroll-mt-24" data-testid={`terms-section-${id}`}>
      <h2 className="font-display text-2xl font-bold tracking-tight mt-12 mb-4">{title}</h2>
      <div className="prose prose-zinc max-w-none text-zinc-700 leading-relaxed space-y-3">
        {children}
      </div>
    </section>
  );
}

export default function Terms() {
  const updated = "17 February 2026";
  return (
    <DoospLayout>
      <section className="border-b border-zinc-200 bg-gradient-to-b from-white to-zinc-50">
        <div className="max-w-4xl mx-auto px-6 py-20">
          <div className="text-xs font-mono uppercase tracking-widest text-red-800 mb-4">
            Legal · Terms of use
          </div>
          <h1 className="font-display text-5xl md:text-6xl font-bold tracking-tight leading-[1.05]" data-testid="terms-heading">
            Terms of Service
          </h1>
          <div className="text-xs font-mono text-zinc-500 mt-4">Last updated: {updated}</div>
        </div>
      </section>

      <div className="max-w-4xl mx-auto px-6 py-16 pb-24">
        {/* Table of contents */}
        <nav className="text-sm text-zinc-600 mb-10 border-l-2 border-red-800 pl-5 space-y-1.5" aria-label="Contents">
          {[
            ["nature", "1. Nature of IMCAC & DOOSP"],
            ["credential", "2. Nature of the credential"],
            ["eligibility", "3. Eligibility & registration"],
            ["conduct", "4. Assessment conduct & anti-cheating"],
            ["fees", "5. Fees, payments & refunds"],
            ["revocation", "6. Certificate revocation"],
            ["ip", "7. Intellectual property"],
            ["liability", "8. Liability & warranties"],
            ["law", "9. Governing law"],
            ["contact", "10. Contact"],
          ].map(([id, label]) => (
            <div key={id}>
              <a href={`#${id}`} className="hover:text-red-800">{label}</a>
            </div>
          ))}
        </nav>

        <Section id="nature" title="1. Nature of IMCAC & DOOSP">
          <p>
            The International Medical Competency Accreditation Council (&ldquo;<strong>IMCAC</strong>&rdquo;) and its
            department, the Department of Occupational Safety Programs (&ldquo;<strong>DOOSP</strong>&rdquo;),
            operate as an <strong>independent competency assessment and certification body</strong>.
            IMCAC is a registered Section 8 company under the Companies Act, 2013, India, operating as a not-for-profit organisation.
          </p>
          <p>
            IMCAC is <strong>not</strong> a licensing authority, regulator, degree-granting institution, or medical council. IMCAC is not accredited by, endorsed by, or affiliated with OSHA, the U.S. Department of Labor, the CDC, the WHO, or any government or professional council. Alignment of our curriculum to any published standard reflects content coverage only — not accreditation, endorsement, or recognition by that body.
          </p>
        </Section>

        <Section id="credential" title="2. Nature of the credential">
          <p>
            Certificates issued by IMCAC/DOOSP are <strong>competency-based credentials</strong> confirming that the named holder successfully completed a competency-based assessment on the topics indicated. Certificates are valid for two (2) calendar years from the date of issue.
          </p>
          <p>
            IMCAC/DOOSP certification <strong>does not</strong> constitute a license to practice, an academic degree, a medical qualification, or a substitute for registration or licensure imposed by any national or professional authority. Users must independently confirm the requirements of their role, employer, and jurisdiction.
          </p>
        </Section>

        <Section id="eligibility" title="3. Eligibility & registration">
          <p>
            Registration is open to individuals aged 18 and above. You are responsible for supplying accurate and current personal information. Certificates are issued in the exact name entered during registration; corrections after issue are subject to a re-issue fee.
          </p>
          <p>
            You must not share your account credentials, take the assessment on behalf of another person, or permit another person to take the assessment on your behalf. Doing so voids the credential and may lead to permanent revocation without refund.
          </p>
        </Section>

        <Section id="conduct" title="4. Assessment conduct & anti-cheating">
          <p>
            The final exam is a 50-item multiple-choice assessment plus an AI-graded case-study response, taken under a 30-minute timer. The pass threshold is 80%. You may retake the exam without limit; each attempt draws a fresh random sample of questions and a fresh case study.
          </p>
          <p>
            Use of unauthorised assistance, automation tools, or content scraping is prohibited. IMCAC reserves the right to invalidate attempts, void certificates, and terminate accounts where cheating is detected.
          </p>
        </Section>

        <Section id="fees" title="5. Fees, payments & refunds">
          <p>
            Enrollment, all learning modules, the AI tutor, practice cases, and the final assessment are <strong>free of charge</strong>. A one-time issuance fee applies only when you choose to unlock and download a paid PDF certificate:
          </p>
          <ul className="list-disc pl-6 space-y-1">
            <li>Individual certificate: <strong>USD 30</strong> per credential</li>
            <li>Corporate bulk orders: tiered discounts starting at 10 seats</li>
          </ul>
          <p>
            <strong>Refund policy.</strong> Because the certificate is a digital, on-demand credential issued immediately after payment, all sales are <strong>final</strong>. Exceptions may be granted at IMCAC&rsquo;s sole discretion for (a) duplicate charges, (b) technical failures preventing certificate issuance, or (c) fraud reported within 7 days. Refund requests must be sent to{" "}
            <a href="mailto:admin@imcac.org" className="text-red-800 underline">admin@imcac.org</a> with the order reference. Approved refunds are returned to the original payment method within 5–10 business days.
          </p>
        </Section>

        <Section id="revocation" title="6. Certificate revocation">
          <p>
            IMCAC may revoke a certificate at any time upon evidence of any of the following:
          </p>
          <ul className="list-disc pl-6 space-y-1">
            <li>Fraudulent registration data (false name, impersonation)</li>
            <li>Assessment cheating or use of automation</li>
            <li>Payment charge-back or fraud</li>
            <li>Court or regulator order</li>
          </ul>
          <p>
            Revoked certificates are marked as invalid on the public{" "}
            <Link to="/verify" className="text-red-800 underline">/verify</Link>{" "}
            page. The certificate holder will be notified by email. Revocation does not entitle the holder to a refund.
          </p>
        </Section>

        <Section id="ip" title="7. Intellectual property">
          <p>
            All course content, question banks, case studies, the AI tutor prompts, certificate designs, and this website are the intellectual property of IMCAC. You are granted a personal, non-transferable license to use the platform for the purpose of completing your own assessment. You may share your issued certificate PDF but may not modify, resell, or redistribute course content in any form.
          </p>
        </Section>

        <Section id="liability" title="8. Liability & warranties">
          <p>
            The platform is provided on an <strong>&ldquo;as-is&rdquo;</strong> and{" "}
            <strong>&ldquo;as-available&rdquo;</strong> basis. To the maximum extent permitted by law, IMCAC disclaims all warranties, express or implied, including fitness for a particular purpose, merchantability, and non-infringement. IMCAC does not warrant that any employer, regulator, or third party will accept the certificate.
          </p>
          <p>
            IMCAC&rsquo;s aggregate liability for any claim arising from your use of the platform is limited to the fees you have paid IMCAC in the twelve (12) months preceding the claim.
          </p>
        </Section>

        <Section id="law" title="9. Governing law">
          <p>
            These Terms are governed by the laws of India. Any dispute shall be resolved by the courts having jurisdiction over the registered office of IMCAC.
          </p>
        </Section>

        <Section id="contact" title="10. Contact">
          <p>
            Questions about these Terms:{" "}
            <a href="mailto:admin@imcac.org" className="text-red-800 underline">admin@imcac.org</a>.
          </p>
        </Section>
      </div>
    </DoospLayout>
  );
}
